import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { captureSurveyStarted } from '@/lib/analytics'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { SurveyEntrySkeleton } from '@/shared/components/skeletons'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useStartProviderResponse } from '../api/instrument-info-mutations'
import {
  useProviderInstrumentInfo,
  useProviderInstrumentStatus,
} from '../api/instrument-info-queries'
import { InstrumentInfoStatusAlert } from '../components/InstrumentInfoStatusAlert'
import { SurveyCompletionView } from '../components/survey-execution/SurveyCompletionView'
import { SurveyAvailabilityBadge } from '../components/SurveyAvailabilityBadge'
import { InstrumentCapacityDialog } from '../components/InstrumentCapacityDialog'
import { InstrumentPausedDialog } from '../components/InstrumentPausedDialog'
import { SurveyEntryActions } from '../components/SurveyEntryActions'
import { SurveyEntryHeader } from '../components/SurveyEntryHeader'
import { SurveyEntryScreen } from '../components/SurveyEntryScreen'
import { SurveyEntryStatsGrid } from '../components/SurveyEntryStatsGrid'
import {
  deriveInstrumentInfoViewState,
  deriveSurveyAvailabilityBadge,
  isInstrumentInfoLoadFailed,
  isInstrumentNotFoundError,
  isInstrumentPausedError,
  showContinueSurveyCta,
  showStartSurveyCta,
} from '../lib/instrument-info-state'
import { useSurveyEntryCompletionChrome } from '../lib/SurveyEntryChromeContext'
import { useSpProfile } from '@/features/rewards/api'
import {
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  isGuestSurveyClaimJourney,
  isPendingSurveyUser,
  persistSurveyFirstOnboardingHandoff,
} from '../lib/pending-survey-session'
import {
  isNonSignupSurveyAutostart,
  NON_SIGNUP_SURVEY_AUTOSTART_PARAM,
} from '../lib/instrument-link-routing'
import { resolveSurveyCompletionVariant } from '../lib/survey-completion-variant'
import { SignupShareThemeProvider } from '../styles/signupShareTheme'
import { useToast } from '@/shared/toast/ToastProvider'
import { notifySurveySessionTimedOut } from '../lib/handle-survey-session-blocked'
import { mapStartSurveyError } from '../lib/map-start-survey-error'
import type {
  InstrumentInfoEntryViewState,
  SurveyExecutionLocationState,
} from '../lib/types'
import {
  surveyEntryCardBodySx,
  surveyEntryLoadingSx,
  surveyEntryStatusAlertSx,
} from '../styles/surveyEntryStyles'

const AUTOSTART_STORAGE_PREFIX = 'bf-nonsignup-survey-autostart:'

export function InstrumentInfoPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const autoStartRequested = isNonSignupSurveyAutostart(searchParams.toString())
  const autoStartHandledKey = useRef<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = useSessionStore((s) => s.user?.id)
  const userGuest = useSessionStore((s) => isGuestSurveyClaimJourney(s.user))
  const surveyFirstJourney = useSessionStore((s) => isPendingSurveyUser(s.user))
  const wrapGuestTheme = (node: ReactElement) =>
    userGuest ? <SignupShareThemeProvider>{node}</SignupShareThemeProvider> : node

  const infoQuery = useProviderInstrumentInfo(instrumentId)
  const statusQuery = useProviderInstrumentStatus(instrumentId)
  const { data: profile } = useSpProfile()
  const underReview = isEkycUnderReview(profile?.ekycStatus)
  const startMutation = useStartProviderResponse()
  const { showToast } = useToast()

  const [startErrorMessage, setStartErrorMessage] = useState<string | null>(
    null
  )
  const [viewStateOverride, setViewStateOverride] =
    useState<InstrumentInfoEntryViewState | null>(null)

  const info = infoQuery.data
  const status = statusQuery.data

  useDocumentTitle(info?.name ?? 'Survey')

  useEffect(() => {
    if (!info || !instrumentId) return
    posthog.capture('survey_viewed', {
      instrument_id: instrumentId,
      survey_name: info.name,
      reward_enabled: info.reward.enabled,
    })
  }, [info, instrumentId])

  const infoLoadFailed = isInstrumentInfoLoadFailed({
    info,
    infoLoading: infoQuery.isLoading,
    infoError: infoQuery.isError,
  })

  const viewState =
    viewStateOverride ??
    (info ? deriveInstrumentInfoViewState({ info, status }) : 'error')

  const showPausedDialog =
    viewState === 'paused' ||
    (statusQuery.isError && isInstrumentPausedError(statusQuery.error))

  useSurveyEntryCompletionChrome(viewState === 'alreadyCompleted')

  const availabilityBadge = info
    ? deriveSurveyAvailabilityBadge({
        info,
        status,
        statusError: statusQuery.isError,
        statusLoading: statusQuery.isLoading,
      })
    : 'unavailable'

  const statusReady =
    Boolean(status) && !statusQuery.isLoading && !statusQuery.isError
  const canStart = showStartSurveyCta({ viewState, status })
  const canContinue = showContinueSurveyCta({ status, viewState })
  const activeResponseId =
    status?.activeResponseId ?? status?.resumableResponseId ?? null

  const navigateToSurvey = useCallback(
    (responseId: string, definition?: unknown) => {
      if (!instrumentId) return
      const state: SurveyExecutionLocationState = {
        responseId,
        instrumentId,
        definition,
      }
      navigate(`/instrument/${instrumentId}/survey`, { state })
    },
    [instrumentId, navigate]
  )

  const runStart = useCallback(async () => {
    if (!instrumentId) return
    setStartErrorMessage(null)
    if (underReview) {
      setStartErrorMessage(EKYC_UNDER_REVIEW_BLOCK_MESSAGE)
      return
    }
    try {
      const result = await startMutation.mutateAsync(instrumentId)
      captureSurveyStarted({
        surveyId: instrumentId,
        surveyName: info?.name,
        campaignId: result.response.campaignId ?? info?.campaignId,
        responseId: result.response.responseId,
        rewardAmount: info?.reward.amount,
      })
      navigateToSurvey(result.response.responseId, result.definition)
    } catch (error) {
      const mapped = mapStartSurveyError(error)
      if (mapped.sessionTimeout) {
        notifySurveySessionTimedOut(showToast, navigate, mapped.message)
        return
      }
      setStartErrorMessage(mapped.message)
      if (mapped.viewStateOverride) {
        setViewStateOverride(mapped.viewStateOverride)
        runDetached(
          queryClient.invalidateQueries({
            queryKey: queryKeys.spInstrument.status(instrumentId),
          }),
        )
      }
    }
  }, [
    info,
    instrumentId,
    navigate,
    navigateToSurvey,
    queryClient,
    showToast,
    startMutation,
    underReview,
  ])

  const handleContinueClick = useCallback(() => {
    if (!activeResponseId) return
    posthog.capture('survey_continued', {
      instrument_id: instrumentId,
      survey_name: info?.name,
      response_id: activeResponseId,
    })
    navigateToSurvey(activeResponseId)
  }, [activeResponseId, instrumentId, info, navigateToSurvey])

  const handleStartSurveyClick = useCallback(() => {
    runDetached(runStart())
  }, [runStart])

  useEffect(() => {
    if (!autoStartRequested || !instrumentId || !info) return
    if (infoQuery.isLoading || !infoQuery.isFetched) return
    if (statusQuery.isLoading || !statusQuery.isFetched) return
    const storageKey = `${AUTOSTART_STORAGE_PREFIX}${location.key}`
    if (autoStartHandledKey.current === location.key) return
    if (sessionStorage.getItem(storageKey) === '1') return
    autoStartHandledKey.current = location.key
    sessionStorage.setItem(storageKey, '1')
    const next = new URLSearchParams(searchParams)
    next.delete(NON_SIGNUP_SURVEY_AUTOSTART_PARAM)
    setSearchParams(next, { replace: true })
    if (canContinue && activeResponseId) {
      handleContinueClick()
      return
    }
    if (canStart) {
      handleStartSurveyClick()
    }
  }, [
    activeResponseId,
    autoStartRequested,
    canContinue,
    canStart,
    handleContinueClick,
    handleStartSurveyClick,
    info,
    infoQuery.isFetched,
    infoQuery.isLoading,
    instrumentId,
    location.key,
    searchParams,
    setSearchParams,
    statusQuery.isFetched,
    statusQuery.isLoading,
  ])

  const startPending = startMutation.isPending

  if (infoQuery.isLoading) {
    return wrapGuestTheme(
      <SurveyEntryScreen>
        <Box sx={surveyEntryLoadingSx}>
          <SurveyEntrySkeleton />
        </Box>
      </SurveyEntryScreen>,
    )
  }

  if (infoQuery.isError && isInstrumentNotFoundError(infoQuery.error)) {
    if (!statusQuery.isFetched) {
      return wrapGuestTheme(
        <SurveyEntryScreen>
          <Box sx={surveyEntryLoadingSx}>
            <SurveyEntrySkeleton />
          </Box>
        </SurveyEntryScreen>,
      )
    }
    if (showPausedDialog) {
      return wrapGuestTheme(<InstrumentPausedDialog open />)
    }
    return <Navigate to="/" replace />
  }

  if (infoLoadFailed || !info) {
    return wrapGuestTheme(
      <SurveyEntryScreen>
        <Box
          sx={surveyEntryCardBodySx}
          data-testid="survey-entry-load-error"
          data-survey-entry-card-body
        >
          <SurveyAvailabilityBadge variant="unavailable" />
          <Alert severity="error" sx={surveyEntryStatusAlertSx}>
            Unable to load survey details. Please try again.
          </Alert>
          <Button variant="outlined" onClick={() => infoQuery.refetch()}>
            Retry
          </Button>
        </Box>
      </SurveyEntryScreen>,
    )
  }

  if (viewState === 'alreadyCompleted') {
    return wrapGuestTheme(
      <SurveyCompletionView
        variant={resolveSurveyCompletionVariant({
          justSubmitted: false,
          guestClaimJourney: userGuest,
        })}
        reward={info.reward}
        instrumentRewardEnabled={info.reward.enabled}
        onClaimNow={() => navigate('/rewards')}
        onHome={() => navigate('/')}
        webSurveySignupMode={userGuest}
        surveyFirstJourney={surveyFirstJourney}
        onCreateAccount={() => {
          persistSurveyFirstOnboardingHandoff(userId, instrumentId)
          navigate(
            surveyFirstJourney
              ? '/instrument/onboarding?surveyFirst=1'
              : '/instrument/onboarding/account',
          )
        }}
      />,
    )
  }

  return wrapGuestTheme(
    <>
      <InstrumentCapacityDialog open={viewState === 'capacityReached'} />
      <InstrumentPausedDialog open={showPausedDialog} />
      <SurveyEntryScreen>
        <Box
          sx={surveyEntryCardBodySx}
          data-testid="survey-entry-card-body"
          data-survey-entry-card-body
        >
          <InstrumentInfoStatusAlert viewState={viewState} />

          <SurveyEntryHeader
            name={info.name}
            description={info.description}
            availability={availabilityBadge}
          />

          <SurveyEntryStatsGrid
            questionCount={info.questionCount}
            estimatedCompletionMinutes={info.estimatedCompletionMinutes}
            reward={info.reward}
          />

          {startErrorMessage ? (
            <Alert
              severity="error"
              sx={surveyEntryStatusAlertSx}
              data-testid="instrument-info-start-error"
            >
              {startErrorMessage}
            </Alert>
          ) : null}
        </Box>

        <SurveyEntryActions
          canStart={canStart}
          canContinue={canContinue}
          startPending={startPending}
          statusReady={statusReady}
          statusLoading={statusQuery.isLoading}
          onStartClick={handleStartSurveyClick}
          onContinueClick={handleContinueClick}
        />
      </SurveyEntryScreen>
    </>,
  )
}

